import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Button } from "@mui/material";
import AmchurCard from "../../components/AmchurCard";
import { type Patient, Visit } from "../../graphql/generated";
import { toFormattedCnic } from "../../domain/cnicUtils";
import usePatientsForVisitsQueries, {
  type VisitAndPatient,
} from "../../hooks/usePatientsForVisitsQueries";
import { setVisit } from "../../state/visitSlice";
import { setPatient } from "../../state/currentPatientSlice";

function MedAssistDashboardRoute() {
  const clinicId = "12345";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { patientsAndVisits } = usePatientsForVisitsQueries({
    clinicId,
    waitingFor: "MA",
  });

  const selectVisit = (visit: Visit, patient: Partial<Patient>) => {
    dispatch(setVisit({ visit }));
    dispatch(setPatient(patient as Patient));
    navigate("/visit-details");
  };

  return (
    <MedAssistDashboardUiView
      allPatients={patientsAndVisits}
      selectVisit={selectVisit}
    />
  );
}
interface MedAssistDashboardUiViewProps {
  allPatients: VisitAndPatient[];
  selectVisit: (visitId: Visit, patient: Partial<Patient>) => void;
}

function MedAssistDashboardUiView({
  allPatients,
  selectVisit,
}: MedAssistDashboardUiViewProps) {
  const intl = useIntl();

  return (
    <Stack direction="row" spacing={2} mt="10px" mx="2em">
      <Stack width="80%" spacing="20px">
        <Typography variant="h3">
          {" "}
          <FormattedMessage id="medAssistDashboard.listHeader" />
        </Typography>
        <AmchurCard
          title={intl.formatMessage({ id: "medAssistDashboard.listSubheader" })}
          role="secondary"
        >
          <AllPatientsListView
            allPatients={allPatients}
            selectVisit={selectVisit}
          />
        </AmchurCard>
      </Stack>
      <Stack width="20%">Buttons go here.</Stack>
    </Stack>
  );
}

interface AllPatientsListViewProps {
  allPatients: VisitAndPatient[];
  selectVisit: (visitId: Visit, patient: Partial<Patient>) => void;
}

function AllPatientsListView({
  allPatients,
  selectVisit,
}: AllPatientsListViewProps) {
  return (
    <Stack spacing={2}>
      {allPatients.map((patientAndVisit) => (
        <PatientView
          patient={patientAndVisit.patient}
          visit={patientAndVisit.visit}
          key={patientAndVisit.visit.visitId}
          selectVisit={selectVisit}
        />
      ))}
    </Stack>
  );
}

interface PatientViewProps {
  patient: Partial<Patient>;
  visit: Visit;
  selectVisit: (visitId: Visit, patient: Partial<Patient>) => void;
}

function PatientView({ patient, visit, selectVisit }: PatientViewProps) {
  const onClick = () => selectVisit(visit, patient);

  const cnic = toFormattedCnic(patient.patientId || "");
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Box width="50%" justifyContent="flex-start">
        <Typography textAlign="start">
          {patient.firstName} {patient.lastName}
        </Typography>
      </Box>
      <Typography>{cnic}</Typography>
      <Button onClick={onClick}>
        <FormattedMessage id="medAssistDashboard.checkVitals" />
      </Button>
    </Stack>
  );
}
export default MedAssistDashboardRoute;
